import { useEffect, useState } from "react";
import { get } from "../../../redux/ApiHelper";
import { Column } from "../../../interfaces/table.interface";
import TableComponent from "../../../components/table/TableComponent";
import { formatDateTime } from "../../../utils/date&time.util";
import { IconButton, Tooltip } from "@mui/material";
import { FaCopy } from "react-icons/fa";
import { successToast } from "../../../utils/toast.util";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { TrialClassPaymentTransactionInterface } from "../../../interfaces/trial-class-payment.interface";

const TrialClassPaymentsTab = () => {
  const [paymentsList, setPaymentsList] = useState<
    TrialClassPaymentTransactionInterface[]
  >([]);
  const [filteredPaymentsList, setFilteredPaymentsList] = useState<
    TrialClassPaymentTransactionInterface[]
  >([]);
  const [searchQuery, setSearchQuery] = useState("");
  const { signedAdmin } = useSelector((state: RootState) => state.admin);

  useEffect(() => {
    const queryParams = new URLSearchParams();
    queryParams.set("type", String(signedAdmin.type));
    queryParams.set("adminId", String(signedAdmin.id));
    get(`/admin/trial-class/payments-all?${queryParams.toString()}`).then(
      (res) => {
        const list = Array.isArray(res) ? res : [];
        setPaymentsList(list);
        setFilteredPaymentsList(list);
      }
    );
  }, [signedAdmin.id, signedAdmin.type]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    const lowerQuery = query.toLowerCase();

    const filteredList = paymentsList?.filter((payment) => {
      const transactionId = payment.transactionId?.toLowerCase() || "";
      const externalTransactionId =
        payment.externalTransactionId?.toLowerCase() || "";
      const paymentDate = formatDateTime(payment?.createdAt);

      return (
        payment._id?.includes(lowerQuery) ||
        payment.amount?.toString().includes(lowerQuery) ||
        payment.parentId?.name?.toLowerCase().includes(lowerQuery) ||
        payment.currency?.toLowerCase().includes(lowerQuery) ||
        transactionId.includes(lowerQuery) ||
        paymentDate.toLowerCase().includes(lowerQuery) ||
        externalTransactionId.includes(lowerQuery)
      );
    });

    setFilteredPaymentsList(filteredList);
  };

  const columns: Column[] = [
    { select: "_id", header: "Payment ID", align: "left" },
    {
      header: "Parent name",
      render: (_value: unknown, row: TrialClassPaymentTransactionInterface) =>
        row?.parentId?.name || "N/A",
    },
    {
      header: "amount",
      render: (_value: unknown, row: TrialClassPaymentTransactionInterface) => (
        <p>{row.amount}</p>
      ),
    },
    { select: "currency", header: "Currency" },
    {
      header: "Status",
      render: (_value: unknown, row: TrialClassPaymentTransactionInterface) => {
        const isCompleted = row?.status === "Completed";

        return (
          <div className="flex items-center">
            <span
              className={`status-badge ${
                isCompleted
                  ? "bg-green-500"
                  : row?.status === "Pending"
                    ? "bg-yellow-500"
                    : "bg-red-500"
              } text-white px-2 py-1 rounded`}
            >
              {row?.status}
            </span>
          </div>
        );
      },
    },
    {
      header: "Transaction ID",
      render: (_value: unknown, row: TrialClassPaymentTransactionInterface) =>
        row?.transactionId || "N/A",
    },
    {
      header: "External Transaction ID",
      render: (_value: unknown, row: TrialClassPaymentTransactionInterface) => {
        const transactionId = row?.externalTransactionId || "N/A";

        if (transactionId === "N/A") {
          return transactionId;
        }

        const handleCopy = () => {
          navigator.clipboard.writeText(transactionId);
          successToast("Transaction ID copied to clipboard!");
        };

        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ marginRight: 8 }}>
              {transactionId.slice(0, 10)}...
            </span>
            <Tooltip title="Copy Transaction ID">
              <IconButton
                onClick={handleCopy}
                size="small"
                aria-label="Copy Transaction ID"
              >
                <FaCopy />
              </IconButton>
            </Tooltip>
          </div>
        );
      },
    },
    {
      header: "Date & Time",
      render: (_value: unknown, row: TrialClassPaymentTransactionInterface) => (
        <p className="whitespace-nowrap">{formatDateTime(row?.createdAt)}</p>
      ),
    },
  ];

  return (
    <div>
      <div className="search-container mb-4">
        <input
          type="text"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-700"
          placeholder="Search payments"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>

      <TableComponent
        columns={columns.filter((item) => {
          if (
            signedAdmin.type === "franchise" &&
            (item.header === "Action" || item.header === "amount breakup")
          ) {
            return false;
          }
          return true;
        })}
        data={filteredPaymentsList}
      />
    </div>
  );
};

export default TrialClassPaymentsTab;
